/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  dedupeEvaluationsToLatestVersions,
  resolveRubricVersion
} from '../../../../common/util/evaluation/evalRubricVersionUtils';
import {
  getBrowserTimeZone,
  hasCustomRange,
  normalizeCustomEvalSummary,
  resolveCustomEvalNoticeKey
} from '../../../../common/util/evaluation/evalCustomRangeUtils';
import * as constants from '../../../../common/constants';
import canAccessEvalReport from '../effectiveness/effectivenessUtils';
import * as RubricsConstants from '../../constants/RubricsConstants';

dayjs.extend(utc);
dayjs.extend(timezone);
export { getBrowserTimeZone };
export const resolveRubricsGraphVariant = (tab) => {
  const searchConfig = Array.isArray(tab?.defaultEffectivenessSearchConfig) ? tab.defaultEffectivenessSearchConfig : [];
  const entityIdentifierConfig = searchConfig.find((config) => config?.component === constants.ENTITY_IDENTIFIER_FIELD);
  return entityIdentifierConfig?.variants?.[RubricsConstants.RUBRICS_ENTITY_TYPE] ?? null;
};
/**
 * The list's columns, in the order the tenant's rubrics tab config gives them via
 * `rubricTableColumn`. Entries carry an `id`, an optional `label` (an i18n key, as `toolTipTitle`
 * elsewhere in that config does) and an optional `visible` flag.
 *
 * Deliberately forgiving: an absent, empty or unrecognised config falls back to the full default
 * set, because a tenant whose config has not been updated must still get a usable table rather
 * than an empty one. Unknown ids are dropped, since nothing could render them.
 */
export const resolveRubricsTableColumns = (tab) => {
  const configured = Array.isArray(tab?.rubricTableColumn) ? tab.rubricTableColumn : [];
  const byId = new Map(RubricsConstants.RUBRICS_DEFAULT_TABLE_COLUMNS.map((column) => [column.id, column]));
  const resolved = configured
    .filter((column) => byId.has(column?.id) && column?.visible !== false)
    .map((column) => ({
      id: column.id,
      label: column.label || byId.get(column.id).label
    }));
  return resolved.length > 0 ? resolved : RubricsConstants.RUBRICS_DEFAULT_TABLE_COLUMNS;
};
export const isRubricsOwnedPath = (pathname) => {
  const segments = String(pathname ?? '')
    .split('?')[0]
    .split('/')
    .filter(Boolean);
  const routeIndex = segments.indexOf(RubricsConstants.RUBRICS_ROUTE);
  if (routeIndex < 0) {
    return false;
  }
  const tail = segments.slice(routeIndex + 1);
  return (
    tail.length === 1 &&
    [
      RubricsConstants.RUBRICS_NEW_ROUTE,
      RubricsConstants.RUBRICS_EDIT_ROUTE,
      RubricsConstants.RUBRICS_SCHEDULE_ROUTE,
      RubricsConstants.RUBRICS_VIEW_ROUTE,
      RubricsConstants.RUBRICS_REPORT_ROUTE,
      RubricsConstants.RUBRICS_RESULT_ROUTE
    ].includes(tail[0])
  );
};
/**
 * Whether the path is anywhere in the rubrics tab: the list itself or one of its owned pages
 * (form, report, result). Leaving this tab is what resets the saved rubrics state.
 * @param {string} pathname
 * @returns {boolean}
 */
export const isRubricsTabPath = (pathname) => {
  const segments = String(pathname ?? '')
    .split('?')[0]
    .split('/')
    .filter(Boolean);
  const routeIndex = segments.indexOf(RubricsConstants.RUBRICS_ROUTE);
  return routeIndex >= 0 && (routeIndex === segments.length - 1 || isRubricsOwnedPath(pathname));
};

/** Stores reset, with the actions to run, when the user leaves the rubrics tab. */
export const RUBRICS_TAB_STORE_RESETS = [
  ['rubricsData', ['clearAll']],
  ['evalGraphRubricsData', ['clearAll']],
  [
    'evalResultData',
    ['clearListView', 'clearSearchView', 'clearResultRequest', 'clearExecutionList', 'clearExecutionDetail']
  ]
];
export const buildRubricsListPath = (tenantName) =>
  `${constants.AI_ADMIN_PATH}/${tenantName}${RubricsConstants.RUBRICS_TAB_PATH}`;
export const buildRubricsFormPath = ({ tenantName, segment, graphId, rubricId }) => {
  const params = new URLSearchParams();
  if (graphId) {
    params.set(constants.GRAPH_ID_KEY, graphId);
  }
  if (rubricId) {
    params.set(constants.RUBRIC_ID_KEY, rubricId);
  }
  const search = params.toString();
  return `${buildRubricsListPath(tenantName)}/${segment}${search ? `?${search}` : ''}`;
};
export const buildRubricsReportPath = (tenantName) =>
  `${buildRubricsListPath(tenantName)}/${RubricsConstants.RUBRICS_REPORT_ROUTE}`;
/**
 * The execution id rides in the query string, not as a path segment: `isRubricsOwnedPath` only
 * recognises a single segment after `rubrics`, and losing that recognition would clear the report's
 * saved view on the way here and leave "Back to results" on an empty page.
 */
export const buildRubricsResultPath = ({ tenantName, executionId }) => {
  const params = new URLSearchParams();
  if (executionId) {
    params.set(constants.EXECUTION_ID_KEY, executionId);
  }
  const search = params.toString();
  return `${buildRubricsListPath(tenantName)}/${RubricsConstants.RUBRICS_RESULT_ROUTE}${search ? `?${search}` : ''}`;
};
export const resolveRubricsPermissions = (tenant) => {
  const list = tenant?.loginPermissions;
  return {
    canView: Boolean(canAccessEvalReport(list)),
    canEdit: Boolean(list?.includes(constants.permissions.EVAL_CAN_EDIT))
  };
};
/**
 * SDK tenants hold no graphs at all: their rubrics belong to the tenant directly, so there is no
 * graph to pick, no graph id to send, and no agent nodes to read eval types from. Absent or false
 * both mean the ordinary tenant-graph-rubric chain.
 */
export const isRubricsSdkEval = (tenant) => tenant?.isSdkEval === true;
const normalizeScheduler = (value) => {
  const dateTime = String(value?.dateTime ?? '').trim();
  const timeZone = String(value?.timeZone ?? '').trim();
  if (!dateTime || !timeZone) {
    return null;
  }
  return {
    dateTime,
    timeZone
  };
};
const schedulerToUtc = (scheduler) => {
  const normalized = normalizeScheduler(scheduler);
  if (!normalized) {
    return null;
  }
  const zoned = dayjs.tz(normalized.dateTime, RubricsConstants.RUBRICS_SCHEDULER_DATETIME_FORMAT, normalized.timeZone);
  return zoned.isValid() ? zoned.utc() : null;
};
/** Today at the current local time with no past range, matching the studio picker's defaults. */
export const buildRubricsSchedulerDefaults = () => {
  const now = dayjs();
  return {
    date: now.startOf(constants.DATE_UNIT_DAY),
    time: now.format(RubricsConstants.RUBRICS_TIME_FORMAT),
    customRange: null
  };
};

/** Notice key for a past-range run, worded for the rubric's saved schedule. */
export const resolveRubricsCustomEvalNoticeKey = ({ scheduler, recurring }) =>
  resolveCustomEvalNoticeKey({ scheduledAt: schedulerToUtc(scheduler), recurring: !!recurring });
export const resolveRubricsSchedulerDraft = (scheduler) => {
  const instant = schedulerToUtc(scheduler);
  if (!instant) {
    return {};
  }
  const local = instant.tz(getBrowserTimeZone());
  return {
    date: local.startOf(constants.DATE_UNIT_DAY),
    time: local.format(RubricsConstants.RUBRICS_TIME_FORMAT)
  };
};
/**
 * The form needs a usable percent in its field, so an unset one becomes the default. The table
 * wants the opposite and keeps '' — hence the coercion lives here rather than in the normalizers.
 */
const toFormSamplePercent = (percent) =>
  percent == null || percent === '' ? String(RubricsConstants.RUBRICS_DEFAULT_SAMPLE_PERCENT) : String(percent);

const normalizeRubricsRow = (item) => {
  const rubricId = item?.rubricId != null && item.rubricId !== '' ? String(item.rubricId) : null;
  if (!rubricId) {
    return null;
  }
  const scheduler = normalizeScheduler(item.scheduler);
  return {
    rubricId,
    rubricVersion: resolveRubricVersion(item.rubricVersion),
    rubricName: String(item.rubricName ?? '').trim(),
    evalTypes: Array.isArray(item.evalTypes) ? item.evalTypes : [],
    evalRubric: String(item.evalRubric ?? ''),
    isRubricValidated: item.isRubricValidated === true,
    evalSamplePercent: item.evalSamplePercent ?? item.samplePercent ?? '',
    scheduler,
    recurring: !!item.recurring,
    isConversational: !!item.isConversational,
    updatedBy: String(item.updatedBy ?? '').trim(),
    customEval: normalizeCustomEvalSummary(item.customEval)
  };
};
/**
 * Shapes a list row into the same contract the detail endpoint returns.
 *
 * The list response already carries every field detail does, so a rubric opened from the list can
 * be seeded from the row the user clicked instead of waiting on a second request for identical
 * data. Detail is still fetched when there is no row to read.
 */
export const rubricsRowToDetail = (row) => {
  if (!row?.rubricId) {
    return null;
  }
  return {
    rubricId: row.rubricId,
    rubricVersion: row.rubricVersion,
    rubricName: row.rubricName,
    evalRubric: row.evalRubric ?? '',
    evalTypes: Array.isArray(row.evalTypes) ? [...row.evalTypes] : [],
    isRubricValidated: row.isRubricValidated === true,
    evalSamplePercent: toFormSamplePercent(row.evalSamplePercent),
    recurring: row.recurring === true,
    isConversational: row.isConversational === true,
    scheduler: row.scheduler ?? null
  };
};

export const normalizeRubricsListResponse = (response) => {
  const body = response?.data ?? response ?? {};
  const rawEvaluations = Array.isArray(body.evaluations) ? body.evaluations : [];
  const evaluations = dedupeEvaluationsToLatestVersions(rawEvaluations.map(normalizeRubricsRow).filter(Boolean));
  return {
    evaluations,
    availableEvalTypes: Array.isArray(body.availableEvalTypes) ? body.availableEvalTypes : []
  };
};
export const normalizeRubricsDetail = (response) => {
  const body = response?.data ?? response ?? {};
  if (body.rubricId == null || body.rubricId === '') {
    return null;
  }
  return {
    rubricId: String(body.rubricId),
    rubricVersion: resolveRubricVersion(body.rubricVersion),
    rubricName: String(body.rubricName ?? '').trim(),
    evalRubric: String(body.evalRubric ?? ''),
    evalTypes: Array.isArray(body.evalTypes) ? body.evalTypes : [],
    isRubricValidated: body.isRubricValidated === true,
    evalSamplePercent: toFormSamplePercent(body.evalSamplePercent),
    recurring: body.recurring === true,
    isConversational: body.isConversational === true,
    scheduler: normalizeScheduler(body.scheduler)
  };
};
export const resolveRubricsStatus = (row) => {
  if (!normalizeScheduler(row?.scheduler)) {
    return RubricsConstants.RUBRICS_STATUS_MANUAL;
  }
  return row.recurring ? RubricsConstants.RUBRICS_STATUS_DAILY : RubricsConstants.RUBRICS_STATUS_ONE_TIME;
};
export const buildRubricsStatusCounts = (rows) => {
  const counts = {
    [RubricsConstants.RUBRICS_STATUS_FILTER_ALL]: rows.length,
    [RubricsConstants.RUBRICS_STATUS_DAILY]: 0,
    [RubricsConstants.RUBRICS_STATUS_ONE_TIME]: 0,
    [RubricsConstants.RUBRICS_STATUS_MANUAL]: 0
  };
  rows.forEach((row) => {
    counts[resolveRubricsStatus(row)] += 1;
  });
  return counts;
};
const nextDailyOccurrence = (anchor, now) => {
  if (!anchor.isBefore(now)) {
    return anchor;
  }
  const wholeDaysElapsed = now
    .startOf(constants.DATE_UNIT_DAY)
    .diff(anchor.startOf(constants.DATE_UNIT_DAY), constants.DATE_UNIT_DAY);
  const candidate = anchor.add(wholeDaysElapsed, constants.DATE_UNIT_DAY);
  return candidate.isBefore(now) ? candidate.add(1, constants.DATE_UNIT_DAY) : candidate;
};
export const resolveRubricsNextRun = (row) => {
  const anchor = schedulerToUtc(row?.scheduler);
  const status = resolveRubricsStatus(row);
  if (!anchor || status === RubricsConstants.RUBRICS_STATUS_MANUAL) {
    return {
      dateTime: '',
      mainKey: 'rubrics.NEXT_RUN_ON_DEMAND',
      subKey: 'rubrics.NEXT_RUN_NO_SCHEDULE'
    };
  }
  const now = dayjs().utc();
  // The instants stay in UTC so the comparisons below are absolute; only the rendered string moves
  // to the reader's own zone, which is why no zone suffix is shown with it.
  const toLocalText = (instant) => instant.tz(getBrowserTimeZone()).format(RubricsConstants.RUBRICS_NEXT_RUN_FORMAT);
  if (status === RubricsConstants.RUBRICS_STATUS_DAILY) {
    return {
      dateTime: toLocalText(nextDailyOccurrence(anchor, now)),
      mainKey: '',
      subKey: 'rubrics.NEXT_RUN'
    };
  }
  return {
    dateTime: toLocalText(anchor),
    mainKey: '',
    subKey: anchor.isBefore(now) ? 'rubrics.NEXT_RUN_RAN_ONCE' : 'rubrics.NEXT_RUN_ONE_TIME_ONLY'
  };
};
const nextRunSortValue = (row) => {
  const anchor = schedulerToUtc(row.scheduler);
  const status = resolveRubricsStatus(row);
  if (!anchor || status === RubricsConstants.RUBRICS_STATUS_MANUAL) {
    return Number.MAX_SAFE_INTEGER;
  }
  const now = dayjs().utc();
  if (status === RubricsConstants.RUBRICS_STATUS_DAILY) {
    return nextDailyOccurrence(anchor, now).valueOf();
  }
  return anchor.isBefore(now) ? Number.MAX_SAFE_INTEGER : anchor.valueOf();
};
export const formatRubricsSamplePercent = (value) => {
  if (value === '' || value == null) {
    return '';
  }
  return String(value);
};
export const parseSamplePercentInput = (rawValue) => {
  if (rawValue === '') {
    return '';
  }
  if (!RubricsConstants.RUBRICS_SAMPLE_PERCENT_PATTERN.test(rawValue)) {
    return null;
  }
  const parsed = parseInt(rawValue, 10);
  return String(
    Math.min(RubricsConstants.RUBRICS_SAMPLE_PERCENT_MAX, Math.max(RubricsConstants.RUBRICS_SAMPLE_PERCENT_MIN, parsed))
  );
};
const matchesQuery = (row, query) =>
  [row.rubricName, ...row.evalTypes].some((value) =>
    String(value ?? '')
      .toLowerCase()
      .includes(query)
  );
export const filterAndSortRubricsRows = ({ rows, query, statusFilter, sort }) => {
  const normalizedQuery = String(query ?? '')
    .trim()
    .toLowerCase();
  const visible = rows.filter((row) => {
    if (statusFilter !== RubricsConstants.RUBRICS_STATUS_FILTER_ALL && resolveRubricsStatus(row) !== statusFilter) {
      return false;
    }
    return !normalizedQuery || matchesQuery(row, normalizedQuery);
  });
  if (sort === RubricsConstants.RUBRICS_SORT_NAME) {
    return [...visible].sort((a, b) => a.rubricName.localeCompare(b.rubricName));
  }
  return [...visible].sort((a, b) => nextRunSortValue(a) - nextRunSortValue(b));
};
/*
 * isRubricValidated records provenance: true only while the text being saved IS the AI suggestion.
 * Applying a suggestion sets it; editing that text afterwards makes it false again, which is why it
 * is derived from a comparison here rather than held as a flag someone has to remember to clear.
 * A save that changes nothing keeps whatever the rubric was loaded with.
 */
export const buildRubricsCreateBody = ({
  rubricName,
  evalRubric,
  evalTypes,
  appliedSuggestion,
  loadedRubric,
  loadedValidated
}) => {
  const text = String(evalRubric ?? '').trim();
  const applied = String(appliedSuggestion ?? '').trim();
  const loaded = String(loadedRubric ?? '').trim();
  const isRubricValidated = (() => {
    if (applied && text === applied) {
      return true;
    }
    return text === loaded ? loadedValidated === true : false;
  })();
  return {
    rubricName: String(rubricName ?? '').trim(),
    evalRubric: text,
    evalTypes: Array.isArray(evalTypes) ? evalTypes : [],
    isRubricValidated
  };
};
export const buildRubricsSchedulerBody = ({ date, time, cadence, samplePercent, isConversational }) => ({
  evalSamplePercent: Number(samplePercent),
  recurring: cadence === RubricsConstants.RUBRICS_CADENCE_DAILY,
  isConversational: !!isConversational,
  evalSchedule: RubricsConstants.RUBRICS_EVAL_SCHEDULE_DAILY,
  scheduler: {
    dateTime: `${date.format(RubricsConstants.RUBRICS_DATE_FORMAT)}T${time}:00`,
    timeZone: getBrowserTimeZone()
  }
});
export const resolveRubricsBasicsErrorKey = ({ rubricName, graphId, evalTypes, isSdkEval }) => {
  if (!String(rubricName ?? '').trim()) {
    return 'rubrics.FORM_NAME_REQUIRED';
  }
  // An SDK tenant has no graph to attach to and no agent nodes to read types from, so neither the
  // graph nor the types can be required of it.
  if (isSdkEval) {
    return null;
  }
  if (!graphId) {
    return 'rubrics.FORM_GRAPH_REQUIRED';
  }
  return evalTypes?.length ? null : 'rubrics.FORM_TYPES_REQUIRED';
};
export const resolveRubricsRubricErrorKey = (rubricText) => {
  const text = String(rubricText ?? '').trim();
  if (!text) {
    return 'rubrics.FORM_RUBRIC_REQUIRED';
  }
  return text.length > RubricsConstants.RUBRICS_RUBRIC_MAX_LENGTH ? 'rubrics.FORM_RUBRIC_TOO_LONG' : null;
};
export const RUBRICS_HOURS = Array.from(
  {
    length: 24
  },
  (_, hour) => hour
);
export const RUBRICS_MINUTES = Array.from(
  {
    length: 60
  },
  (_, minute) => minute
);
export const resolveRubricsAvailableHours = (date) => {
  if (!date?.isValid?.() || !date.isSame(dayjs(), constants.DATE_UNIT_DAY)) {
    return RUBRICS_HOURS;
  }
  const currentHour = dayjs().hour();
  return RUBRICS_HOURS.filter((hour) => hour >= currentHour);
};
export const resolveRubricsAvailableMinutes = (date, hour) => {
  const now = dayjs();
  if (!date?.isValid?.() || !date.isSame(now, constants.DATE_UNIT_DAY) || hour > now.hour()) {
    return RUBRICS_MINUTES;
  }
  if (hour < now.hour()) {
    return [];
  }
  return RUBRICS_MINUTES.filter((minute) => minute >= now.minute());
};
export const parseRubricsTime = (time) => {
  const [hour, minute] = String(time ?? '').split(':');
  const parsedHour = Number(hour);
  const parsedMinute = Number(minute);
  return {
    hour: Number.isInteger(parsedHour) && parsedHour >= 0 && parsedHour < 24 ? parsedHour : 0,
    minute: Number.isInteger(parsedMinute) && parsedMinute >= 0 && parsedMinute < 60 ? parsedMinute : 0
  };
};
export const formatRubricsTime = (hour, minute) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
const resolveRubricsSampleErrorKey = (samplePercent) => {
  const percent = Number(samplePercent);
  const isValid =
    samplePercent !== '' &&
    Number.isFinite(percent) &&
    percent >= RubricsConstants.RUBRICS_SAMPLE_PERCENT_MIN &&
    percent <= RubricsConstants.RUBRICS_SAMPLE_PERCENT_MAX;
  return isValid ? null : 'rubrics.FORM_SAMPLE_ERROR';
};

/** A past range runs immediately, so only the sample % needs checking; otherwise date, time and sample. */
export const resolveRubricsSchedulerErrorKey = ({ date, time, samplePercent, customRange }) => {
  if (hasCustomRange(customRange)) {
    return resolveRubricsSampleErrorKey(samplePercent);
  }
  if (!date?.isValid?.()) {
    return 'rubrics.FORM_DATE_REQUIRED';
  }
  if (!/^\d{2}:\d{2}$/.test(String(time ?? ''))) {
    return 'rubrics.FORM_TIME_REQUIRED';
  }
  const sampleErrorKey = resolveRubricsSampleErrorKey(samplePercent);
  if (sampleErrorKey) {
    return sampleErrorKey;
  }
  const [hour, minute] = String(time).split(':');
  const instant = date.hour(Number(hour)).minute(Number(minute)).second(0).millisecond(0);
  return instant.isBefore(dayjs(), 'minute') ? 'rubrics.FORM_DATE_IN_PAST' : null;
};
export const resolveRubricsCompletedSteps = ({
  isExisting,
  activeStep,
  hasSchedule,
  hasLeftBasics,
  isBasicsComplete,
  isRubricComplete
}) => {
  const done = [];
  if (isExisting) {
    done.push(RubricsConstants.RUBRICS_STEP_BASICS, RubricsConstants.RUBRICS_STEP_RUBRIC);
    if (hasSchedule) done.push(RubricsConstants.RUBRICS_STEP_SCHEDULER);
  } else {
    if (hasLeftBasics && isBasicsComplete) done.push(RubricsConstants.RUBRICS_STEP_BASICS);
    if (isRubricComplete) done.push(RubricsConstants.RUBRICS_STEP_RUBRIC);
  }
  return done.filter((step) => step !== activeStep);
};
